import { useCallback, useEffect, useState } from "react"
import { Bell } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import {
    SegmentedControl,
    SegmentedControlItem,
} from "@/components/ui/segmented-control"
import { requestNotificationPermission } from "@/lib/browserNotifications"
import {
    getNotificationPrefs,
    setNotificationPrefs,
    type NotificationFormat,
    type NotificationPrefs,
} from "@/lib/notificationPrefs"

function isDenied(): boolean {
    return typeof Notification !== "undefined" && Notification.permission === "denied"
}

export function NotificationSettings({
    registerTourTarget,
}: {
    registerTourTarget?: (id: string, el: HTMLElement | null) => void
} = {}) {
    const [prefs, setPrefs] = useState<NotificationPrefs>(getNotificationPrefs)
    const [denied, setDenied] = useState<boolean>(isDenied)

    const bellRef = useCallback(
        (el: HTMLElement | null) => {
            registerTourTarget?.("notifications", el)
        },
        [registerTourTarget],
    )

    const ensurePermission = async () => {
        const permission = await requestNotificationPermission()
        setDenied(permission === "denied")
    }

    useEffect(() => {
        let cancelled = false
        void requestNotificationPermission().then((permission) => {
            if (!cancelled) setDenied(permission === "denied")
        })
        return () => {
            cancelled = true
        }
    }, [])

    const setFormat = (format: NotificationFormat) => {
        const next: NotificationPrefs = { ...prefs, enabled: true, format }
        setPrefs(next)
        setNotificationPrefs(next)
    }

    return (
        <Popover>
            {/* Register on a wrapper — nested Tooltip/Popover asChild thrash callback refs (#185). */}
            <span
                ref={bellRef}
                className="inline-flex"
                data-tour-target="notifications"
            >
            <Tooltip>
                <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                        <Button
                            type="button"
                            variant="default"
                            size="icon"
                            className="h-9 w-9"
                            aria-label="Уведомления копайлота"
                            onClick={() => void ensurePermission()}
                        >
                            <Bell className="h-4 w-4" />
                        </Button>
                    </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>Уведомления</TooltipContent>
            </Tooltip>
            </span>
            <PopoverContent align="end" className="w-80 p-4">
                <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                        <Bell className="h-4 w-4 text-neutral-text-800" />
                        <span className="text-body1 font-medium text-neutral-text-900">
                            Уведомления копайлота
                        </span>
                    </div>

                    {denied && (
                        <p className="text-caption text-neutral-text-500">
                            Уведомления заблокированы в браузере — разрешите их для сайта в настройках.
                        </p>
                    )}

                    <div className="flex flex-col gap-1.5">
                        <span className="text-caption text-neutral-text-500">Формат</span>
                        <SegmentedControl
                            type="single"
                            value={prefs.format}
                            onValueChange={(format) => {
                                if (!format) return
                                setFormat(format as NotificationFormat)
                            }}
                            className="flex gap-1 rounded-m bg-neutral-background-100 p-1"
                        >
                            <SegmentedControlItem value="color_text" className="flex-1">
                                Цвет + текст
                            </SegmentedControlItem>
                            <SegmentedControlItem value="color" className="flex-1">
                                Только цвет
                            </SegmentedControlItem>
                        </SegmentedControl>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    )
}
